import { isDefined, isNotDefined } from './core.js';
import { listToMap } from './transform.js';

/**
 * Identify if two list are the same
 *
 * @param list1
 * @param list2
 *
 * @remarks
 * The function assumes that the elements aren't mutated
 */
function isListEqual(list1, list2) {
  return list1.length === list2.length && list1.every((d, i) => d === list2[i]);
}

/**
 * Get a random item from the list
 *
 * @param items
 */
function getRandomFromList(items) {
  return items[Math.floor(Math.random() * items.length)];
}

/**
 * Get an element around certain index in a list
 *
 * @param list
 * @param index
 */
function getElementAround(list, index) {
  if (index + 1 < list.length) {
    return list[index + 1];
  }
  if (index - 1 >= 0) {
    return list[index - 1];
  }
  return undefined;
}

/**
 * Get a defined element around certain index in a list
 *
 * @param list
 * @param index
 */
function getDefinedElementAround(list, currentIndex) {
  let i;
  let j;
  for (i = currentIndex - 1; i >= 0; i -= 1) {
    if (list[i] !== undefined) {
      break;
    }
  }
  for (j = currentIndex + 1; j < list.length; j += 1) {
    if (list[j] !== undefined) {
      break;
    }
  }
  if (i < 0 && j >= list.length) {
    // console.warn('none');
    return undefined;
  }
  if (i < 0 && j < list.length) {
    // console.warn('none on left');
    return list[j];
  }
  if (j >= list.length && i >= 0) {
    // console.warn('none on right');
    return list[i];
  }
  const iDist = currentIndex - i;
  const jDist = j - currentIndex;
  if (jDist > iDist) {
    return list[i];
  }
  return list[j];
}

/**
 * Get a duplicate count for each element in a list
 *
 * @param list
 * @param keySelector get key from an element
 *
 * @returns map of element key and duplicate count
 *
 * @remarks
 * The map only includes element for which there is a duplicate
 */

function getDuplicates(list, keySelector) {
  if (!list) {
    return undefined;
  }
  const counts = listToMap(list, keySelector, (_, key, __, acc) => {
    const value = acc[key];
    return isDefined(value) ? value + 1 : 1;
  });
  return Object.keys(counts).filter(key => counts[key] > 1);
}

/**
 * Find difference between two list
 *
 * @param listA
 * @param listB
 * @param keySelector get key from an element
 *
 * @returns list of added, modified, removed and unmodified elements
 */
function findDifferenceInList(listA, listB, keySelector) {
  const modified = [];
  const added = [];
  const removed = [];
  const unmodified = [];
  const mapA = listToMap(listA, keySelector, e => e);
  listB.forEach((elem, i) => {
    const key = keySelector(elem, i);
    if (isNotDefined(mapA[key])) {
      added.push(elem);
    } else if (mapA[key] !== elem) {
      modified.push({
        old: mapA[key],
        new: elem
      });
    } else {
      unmodified.push(elem);
    }
  });
  const mapB = listToMap(listB, keySelector, e => e);
  listA.forEach((elem, i) => {
    const key = keySelector(elem, i);
    if (isNotDefined(mapB[key])) {
      removed.push(elem);
    }
  });
  return {
    added,
    modified,
    removed,
    unmodified
  };
}

/**
 * Find unique items from a list
 *
 * @param list
 * @param getItemHash method to get id to check uniqueness
 *
 * @remarks
 * If getItemHash is not supplied, comparision is done by casting items in list
 * to string
 */

function unique(list, getItemHash) {
  if (isNotDefined(list)) {
    return undefined;
  }
  const memory = {};
  const arrWithUnique = [];
  list.forEach(item => {
    const hash = getItemHash ? getItemHash(item) : JSON.stringify(item);
    if (!memory[hash]) {
      memory[hash] = true;
      arrWithUnique.push(item);
    }
  });
  // NOTE: just return original array if all elements were unique
  if (list.length === arrWithUnique.length) {
    return list;
  }
  return arrWithUnique;
}
function max(list, getNumericValue) {
  if (!list || list.length <= 0) {
    return undefined;
  }
  const values = list.reduce((acc, item) => {
    const {
      maxValue
    } = acc;
    const myValue = getNumericValue(item);
    return isDefined(myValue) && (isNotDefined(maxValue) || myValue > maxValue) ? {
      maxValue: myValue,
      maxItem: item
    } : acc;
  }, {
    maxItem: undefined,
    maxValue: undefined
  });
  return values.maxItem;
}
function min(list, getNumericValue) {
  if (!list || list.length <= 0) {
    return undefined;
  }
  const values = list.reduce((acc, item) => {
    const {
      minValue
    } = acc;
    const myValue = getNumericValue(item);
    return isDefined(myValue) && (isNotDefined(minValue) || myValue < minValue) ? {
      minValue: myValue,
      minItem: item
    } : acc;
  }, {
    minItem: undefined,
    minValue: undefined
  });
  return values.minItem;
}

export { findDifferenceInList, getDefinedElementAround, getDuplicates, getElementAround, getRandomFromList, isListEqual, max, min, unique };
//# sourceMappingURL=list.js.map
